import { ReactElement, useCallback, useEffect } from 'react';
import loadable from '@loadable/component';

import { Grid, Heading, Text } from '@simplywallst/ui-core';
import { useUserAgent } from '@simplywallst/hooks/useUserAgent';
import { isIncompleteManualPortfolio } from '../PortfolioManual/utils';
import {
  useManualPortfolioContext,
  useManualPortfolioTracking,
} from '../PortfolioManual';
import BrazeContentCard from '@/components/BrazeContentCard/BrazeContentCard';

import type {
  HoldingFragmentType,
  Portfolio,
} from '@/hooks/portfolio/usePortfolio';
import { useIsMobileScreen } from '@/hooks/useMedia';

import { usePanelHoldingId } from '../../hooks/usePanelHoldingId';

import {
  HoldingsTableActions,
  DemoHoldingsTableActions,
} from '../HoldingsTable';
import {
  LinkedPortfolioFeedbackBanner,
  ManualPortfolioFeedbackBanner,
} from '../FeedbackBanner';

import {
  HoldingsCount,
  HoldingsTableSection,
  TableFooter,
  TableHeader,
} from './styled';

const HoldingsTable = loadable(() => import('../HoldingsTable'), {
  resolveComponent: (c) => c.HoldingsTable,
});
const DemoHoldingsTable = loadable(() => import('../HoldingsTable'), {
  resolveComponent: (c) => c.DemoHoldingsTable,
});
const PortfolioPanel = loadable(() => import('../PortfolioPanel'), {
  resolveComponent: (c) => c.PortfolioPanel,
});

type Props = {
  portfolio: Portfolio;
  excludedHoldings?: HoldingFragmentType[];
  sortedHoldings: HoldingFragmentType[];
};
type LayoutProps = {
  portfolio: Portfolio;
  panelHoldingId: HoldingFragmentType['id'] | null;
  tableActions: ReactElement;
  table: ReactElement;
  panel: ReactElement | null;
};

export const HoldingsOverviewLayout = ({
  portfolio,
  panelHoldingId,
  tableActions,
  table,
  panel,
}: LayoutProps) => {
  const { isMobile } = useUserAgent();
  const isMobileScreen = useIsMobileScreen({ defaultMatches: isMobile });

  return (
    <>
      {isMobileScreen ? (
        <>
          <HoldingsTableSection data-cy-id="holdings-table">
            <TableHeader
              as="header"
              templateColumns="auto auto"
              justifyContent="space-between"
              gap="8px"
              alignItems="center"
              data-cy-id="holdings-header"
            >
              <Grid templateColumns="auto 24px" gap="8px" alignItems="center">
                <Heading as="h2" typography="baseStrong">
                  Holdings
                </Heading>
                <HoldingsCount placeContent="center">
                  <Text
                    typography="xSmallStrong"
                    color="whiteRGBA"
                    opacity={0.7}
                  >
                    {portfolio.holdings.length}
                  </Text>
                </HoldingsCount>
              </Grid>
              {tableActions}
            </TableHeader>
            {table}
            <BrazeContentCard
              feedType="new-portfolio.bottom"
              anchorTarget="_blank"
            />
            <TableFooter>
              {portfolio.type === 'linked' ? (
                <LinkedPortfolioFeedbackBanner />
              ) : (
                <ManualPortfolioFeedbackBanner />
              )}
            </TableFooter>
          </HoldingsTableSection>
          {panel}
        </>
      ) : (
        <Grid
          templateColumnsLg={panelHoldingId ? 'auto 32%' : 'auto'}
          gap="16px"
        >
          <div>
            <HoldingsTableSection data-cy-id="holdings-table">
              <TableHeader
                as="header"
                templateColumns="auto auto"
                justifyContent="space-between"
                gap="8px"
                alignItems="center"
                data-cy-id="holdings-header"
              >
                <Grid templateColumns="auto 24px" gap="8px" alignItems="center">
                  <Heading as="h2" typography="baseStrong">
                    Holdings
                  </Heading>
                  <HoldingsCount placeContent="center">
                    <Text
                      typography="xSmallStrong"
                      color="whiteRGBA"
                      opacity={0.7}
                    >
                      {portfolio.holdings.length}
                    </Text>
                  </HoldingsCount>
                </Grid>
                {tableActions}
              </TableHeader>
              {table}
              <BrazeContentCard
                feedType="new-portfolio.bottom"
                anchorTarget="_blank"
              />
              <TableFooter>
                {portfolio.type === 'linked' ? (
                  <LinkedPortfolioFeedbackBanner />
                ) : (
                  <ManualPortfolioFeedbackBanner />
                )}
              </TableFooter>
            </HoldingsTableSection>
          </div>
          {panel}
        </Grid>
      )}
    </>
  );
};

export const HoldingsOverview = ({
  portfolio,
  excludedHoldings = [],
  sortedHoldings,
}: Props) => {
  const defaultHoldingId = sortedHoldings[0]?.id ?? null;

  const { updatePortfolio } = useManualPortfolioContext();
  const { trackManualPortfolioEvent } = useManualPortfolioTracking();

  const [panelHoldingId, setPanelHoldingId] = usePanelHoldingId({
    defaultId: defaultHoldingId,
  });

  const handleToggleTableExpansion = useCallback(() => {
    setPanelHoldingId((id) => (id ? null : defaultHoldingId));
  }, [defaultHoldingId, setPanelHoldingId]);

  useEffect(() => {
    if (
      portfolio.type === 'manual' &&
      isIncompleteManualPortfolio(sortedHoldings).incomplete
    ) {
      setPanelHoldingId(null);
    }
  }, [portfolio.id, sortedHoldings, portfolio.type, setPanelHoldingId]);

  return (
    <HoldingsOverviewLayout
      portfolio={portfolio}
      panelHoldingId={panelHoldingId}
      table={
        <HoldingsTable
          portfolioId={portfolio.id}
          excludedHoldings={excludedHoldings}
          holdings={sortedHoldings}
          holdingId={panelHoldingId}
          onHoldingSelect={setPanelHoldingId}
        />
      }
      tableActions={
        <HoldingsTableActions
          type={portfolio.type}
          onUpdatePortfolio={() => {
            trackManualPortfolioEvent({
              modifier: 'edit-holdings',
              action: 'click',
              portfolioId: portfolio.id,
            });
            updatePortfolio({ portfolioId: portfolio.id });
          }}
          onToggleTableExpansion={handleToggleTableExpansion}
        />
      }
      panel={
        panelHoldingId ? (
          <PortfolioPanel
            holdingId={panelHoldingId}
            excludedHoldings={excludedHoldings}
            holdings={sortedHoldings}
            setHoldingId={setPanelHoldingId}
          />
        ) : null
      }
    />
  );
};

export const DemoHoldingsOverview = ({ portfolio, sortedHoldings }: Props) => {
  const defaultHoldingId = sortedHoldings[0]?.id ?? null;

  const [panelHoldingId, setPanelHoldingId] = usePanelHoldingId({
    defaultId: defaultHoldingId,
  });

  const handleToggleTableExpansion = useCallback(() => {
    setPanelHoldingId((id) => (id ? null : defaultHoldingId));
  }, [defaultHoldingId, setPanelHoldingId]);

  useEffect(() => {
    if (
      portfolio.type === 'manual' &&
      isIncompleteManualPortfolio(sortedHoldings).incomplete
    ) {
      setPanelHoldingId(null);
    }
  }, [portfolio.id, sortedHoldings, portfolio.type, setPanelHoldingId]);

  return (
    <HoldingsOverviewLayout
      portfolio={portfolio}
      panelHoldingId={panelHoldingId}
      table={
        <DemoHoldingsTable
          portfolioId={portfolio.id}
          excludedHoldings={[]}
          holdings={sortedHoldings}
          holdingId={panelHoldingId}
          onHoldingSelect={setPanelHoldingId}
        />
      }
      tableActions={
        <DemoHoldingsTableActions
          type={portfolio.type}
          onToggleTableExpansion={handleToggleTableExpansion}
        />
      }
      panel={
        panelHoldingId ? (
          <PortfolioPanel
            holdingId={panelHoldingId}
            excludedHoldings={[]}
            holdings={sortedHoldings}
            setHoldingId={setPanelHoldingId}
          />
        ) : null
      }
    />
  );
};
